import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api, { setAuthTokens, clearAuthTokens, getAccessToken } from '@/lib/api';

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadUser = useCallback(async () => {
    const token = getAccessToken();
    if (!token) { setLoading(false); return; }
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
    } catch (e) {
      // /auth/me failed even after a silent refresh attempt → session is gone.
      clearAuthTokens();
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUser(); }, [loadUser]);

  // React to a fully-expired session (refresh token also invalid/expired).
  useEffect(() => {
    const onExpired = () => setUser(null);
    window.addEventListener('kiteiq:session-expired', onExpired);
    return () => window.removeEventListener('kiteiq:session-expired', onExpired);
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    setAuthTokens(data.token, data.refresh_token);
    setUser(data.user);
    return data.user;
  };

  const register = async (name, email, password, lang, ref) => {
    const { data } = await api.post('/auth/register', {
      name, email, password, lang, ref: ref || undefined, origin: window.location.origin,
    });
    setAuthTokens(data.token, data.refresh_token);
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    clearAuthTokens();
    setUser(null);
  };

  const refreshUser = useCallback(async () => {
    try { const { data } = await api.get('/auth/me'); setUser(data); return data; } catch (e) { return null; }
  }, []);

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};
